import React, { useEffect, useRef, type PropsWithChildren } from 'react'
import { createPortal } from 'react-dom'
import SwiperCore from 'swiper'
import { FreeMode, Navigation, Zoom } from 'swiper/modules'
import { SwiperSlide, Swiper } from 'swiper/react'

import 'swiper/scss'
import 'swiper/scss/pagination'
import 'swiper/css/free-mode'
import 'swiper/scss/navigation'
import 'swiper/css/thumbs'

import type { ReactDialogSliderProps } from '@/types/components/dialog'
import { Icon } from '@/components/Icon'
import { ResponsiveImage } from '@/components/Shared/ResponsiveImage'
import { Video } from '@/components/Shared/Video'
import { useIsClient } from '@/hooks/useIsClient'

import './DialogSlider.scss'

export type Props = ReactDialogSliderProps

export const DialogSlider: React.FC<PropsWithChildren<Props>> = ({
  isOpen,
  onClose,
  imageryCarousel,
  selectedItem,
}) => {
  const isClient = useIsClient()

  const dialogSwiperRef = useRef<SwiperCore | null>(null)
  const ref = useRef<HTMLDialogElement>(null)

  const openDialog = () => {
    ref.current?.show()
    document.body.style.overflow = 'hidden'
  }

  const closeDialog = () => {
    const dialog = ref.current
    document.body.style.overflow = 'auto'
    if (!dialog) return

    dialog?.classList.add('dialog--close')

    const handleAnimationEnd = (event: AnimationEvent) => {
      if (event.animationName !== 'dialog-close') return

      dialog.classList.remove('dialog--close')
      dialog.close()
      dialog.removeEventListener('animationend', handleAnimationEnd)
    }

    dialog.addEventListener('animationend', handleAnimationEnd)
  }

  const nextSlide = () => {
    if (dialogSwiperRef.current) {
      dialogSwiperRef.current.slideNext()
    }
  }
  const prevSlide = () => {
    if (dialogSwiperRef.current) {
      dialogSwiperRef.current.slidePrev()
    }
  }

  useEffect(() => {
    if (isOpen) {
      openDialog()
    } else {
      closeDialog()
    }
  }, [isOpen])

  const handleInnerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
  }

  const classes = ['dialog-slider']

  const handleDialogClose = () => {
    if (dialogSwiperRef.current?.realIndex !== undefined) {
      onClose(dialogSwiperRef.current.realIndex)
    }
  }

  const dialogContent = (
    <dialog
      className={classes.join(' ')}
      ref={ref}
      onClick={handleDialogClose}
      onCancel={handleDialogClose}
    >
      <div className="dialog--slider__inner" onClick={handleInnerClick}>
        <div className="desktop-dialog-slider">
          <button className="left-arrow" onClick={prevSlide}>
            <Icon name="arrow" />
          </button>
          <Swiper
            onSwiper={(swiper) => (dialogSwiperRef.current = swiper)}
            modules={[Navigation, FreeMode, Zoom]}
            slidesPerView={1}
            scrollbar={{ draggable: true }}
            zoom={{ maxRatio: 3, minRatio: 1 }}
            navigation={true}
            loop={true}
            initialSlide={selectedItem}
            key={selectedItem}
          >
            {imageryCarousel.map((carouselItem, index) => {
              return (
                <SwiperSlide key={index} className="dialog-swiper-slide">
                  <div className="swiper-zoom-container">
                    {carouselItem._type === 'component.responsiveImage' && (
                      <ResponsiveImage
                        document={carouselItem}
                        overrides={{
                          objectFit: 'cover',
                          mobileAspectRatio: '1/1',
                          desktopAspectRatio: '1/1',
                          customSizes: [640, 768, 1024, 1200],
                        }}
                      />
                    )}
                    {carouselItem._type === 'component.video' && (
                      <Video
                        videoUrl={carouselItem.video.secureUrl}
                        videoType={carouselItem.videoType}
                        classes="carousel__background-video carousel__background-video--desktop carousel__background-video--mobile"
                        loopVideo={carouselItem.background?.loopVideo}
                        isFirstSection={false}
                        backupImage={carouselItem.backupImage}
                      />
                    )}
                  </div>
                </SwiperSlide>
              )
            })}
          </Swiper>
          <button className="right-arrow" onClick={nextSlide}>
            <Icon name="arrow" />
          </button>
        </div>
        <div className="mobile-dialog-slider-wrapper">
          <Swiper
            slidesPerView="auto"
            direction="vertical"
            freeMode={true}
            modules={[FreeMode, Zoom]}
            zoom={{ maxRatio: 3, minRatio: 1 }}
            className="mobile-dialog-slider"
            key={selectedItem}
          >
            {imageryCarousel.map((carouselItem, index) => {
              return (
                <SwiperSlide key={index} className="dialog-swiper-slide">
                  <div className="swiper-zoom-container">
                    {carouselItem._type === 'component.responsiveImage' && (
                      <ResponsiveImage document={carouselItem} />
                    )}
                    {carouselItem._type === 'component.video' && (
                      <Video
                        videoUrl={carouselItem.video.secureUrl}
                        videoType={carouselItem.videoType}
                        classes="carousel__background-video carousel__background-video--desktop carousel__background-video--mobile"
                        loopVideo={carouselItem.background?.loopVideo}
                        isFirstSection={false}
                        backupImage={carouselItem.backupImage}
                      />
                    )}
                  </div>
                </SwiperSlide>
              )
            })}
          </Swiper>
        </div>
        <button className="dialog--slider__close" onClick={handleDialogClose}>
          <Icon name="cross" />
        </button>
      </div>
    </dialog>
  )

  return isClient ? createPortal(dialogContent, document.body) : null
}
